import { Injectable } from '@angular/core';

@Injectable({
    providedIn: 'root',
})
export class DateService {
    /**
     * Calculates the number of days between two dates.
     *
     * If the end date is before the start date, it returns a negative number.
     * If the end date is after the start date, it returns a positive number.
     * If the end date is the same as the start date, it returns 0.
     *
     * Both dates are normalized to midnight to ensure that time components (hours, minutes, seconds)
     * do not affect the day difference calculation.
     *
     * @param startDate The start date.
     * @param endDate The end date.
     * @returns The number of days between the two dates.
     */
    calculateDaysBetweenDates(startDate: Date, endDate: Date): number {
        // Clone the input dates to avoid mutating the original objects
        const start = new Date(startDate);
        const end = new Date(endDate);

        // Normalize both dates to midnight (00:00:00.000) to ignore time differences
        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);

        // Calculate the difference in milliseconds
        const diffInMs = end.getTime() - start.getTime();

        /**
         * Convert milliseconds to days by dividing by the number of milliseconds in a day.
         *
         * We use Math.trunc instead of Math.floor to ensure that:
         *  - Positive numbers are rounded **toward zero** (e.g., 4.9 → 4)
         *  - Negative numbers are also rounded **toward zero** (e.g., -4.9 → -4)
         *
         * Using Math.floor would round negative numbers away from zero (toward -Infinity),
         * which would incorrectly return one extra day when the result is negative.
         */
        return Math.trunc(diffInMs / (1000 * 60 * 60 * 24));
    }
}
